/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { IServices, TypeServiceEnum } from 'features/salesOrder/types';

interface ServiceSelectorProps {
	services: IServices[];
	selectedServiceIds: string[];
	onChange: (serviceIds: string[]) => void;
	filterByType?: (service: IServices) => boolean;
	disabled?: boolean;
}

const getServiceDescription = (service: IServices): string => {
	if (!service || !service.serviceType) {
		return 'No especificado';
	}

	switch (service.serviceType) {
		case TypeServiceEnum.Accommodation: {
			const destinationCode = (service as any).destination?.code ?? 'N/A';
			const serviceProviderName = (service as any).serviceProvider?.name ?? 'N/A';
			const roomsLength = (service as any).rooms?.length ?? 0;
			return `Alojamiento | ${destinationCode}: ${serviceProviderName} | ${roomsLength} hab`;
		}
		case TypeServiceEnum.Flight: {
			const vcCode = (service as any).vc?.code ?? 'N/A';
			const reference = (service as any).reference ?? 'N/A';
			const passengerIdsLength =
				(service as any).passengersGroupByPassengerType?.reduce(
					(acc: number, group: any) => acc + (group.passengerIds?.length || 0),
					0,
				) || 0;
			return `Vuelo | vc: ${vcCode} | pnr: ${reference} | ${passengerIdsLength} pax`;
		}
		case TypeServiceEnum.FlightAncillary: {
			const flightAncillaryType = (service as any).flightAncillaryType ?? 'N/A';
			const vcCode = (service as any).vc?.code ?? 'N/A';
			const reference = (service as any).reference ?? 'N/A';
			const passengerIdsLength = (service as any).passengerIds?.length || 0;
			return `FlightAncillary | ${flightAncillaryType} | vc: ${vcCode} | pnr: ${reference} | ${passengerIdsLength} pax`;
		}
		case TypeServiceEnum.Other: {
			const otherServiceType = (service as any).otherServiceType ?? 'Otro';
			const serviceName = (service as any).serviceName ?? 'N/A';
			const passengerIdsLength = (service as any).passengerIds?.length || 0;
			return `${otherServiceType}: ${serviceName} | ${passengerIdsLength} pax`;
		}
		case TypeServiceEnum.Transfer: {
			const transferSegment = (service as any).transferSegments?.[0];
			const transferType = transferSegment?.transferType ?? 'N/A';
			const originName = transferSegment?.originName ?? 'N/A';
			const destinationName = transferSegment?.destinationName ?? 'N/A';
			const passengerIdsLength = (service as any).passengerIds?.length || 0;
			return `Transfer | ${transferType} | ${originName} → ${destinationName} | ${passengerIdsLength} pax`;
		}
		default: {
			return 'Tipo de servicio no reconocido';
		}
	}
};

export const ServiceSelector: React.FC<ServiceSelectorProps> = ({
	services,
	selectedServiceIds,
	onChange,
	filterByType,
	disabled = false,
}) => {
	const filteredServices = filterByType ? services.filter(filterByType) : services;

	const handleServiceToggle = (serviceId: string) => {
		if (disabled) return;

		if (selectedServiceIds.includes(serviceId)) {
			onChange(selectedServiceIds.filter((id) => id !== serviceId));
		} else {
			onChange([...selectedServiceIds, serviceId]);
		}
	};

	const handleSelectAll = () => {
		if (disabled) return;

		if (selectedServiceIds.length === filteredServices.length) {
			onChange([]);
		} else {
			onChange(filteredServices.map((s) => s.keyId));
		}
	};

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
					Seleccionar servicios
				</Typography>
				{filteredServices.length > 0 && (
					<FormControlLabel
						control={
							<Checkbox
								checked={selectedServiceIds.length === filteredServices.length && filteredServices.length > 0}
								indeterminate={selectedServiceIds.length > 0 && selectedServiceIds.length < filteredServices.length}
								onChange={handleSelectAll}
								disabled={disabled}
							/>
						}
						label="Seleccionar todos"
					/>
				)}
			</Box>

			{filteredServices.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
					No hay servicios disponibles que cumplan con los filtros seleccionados.
				</Typography>
			) : (
				<FormGroup>
					{filteredServices.map((service) => {
						const isSelected = selectedServiceIds.includes(service.keyId);
						const isInactive = service.enable === false;

						return (
							<FormControlLabel
								key={service.keyId}
								control={
									<Checkbox
										checked={isSelected}
										onChange={() => handleServiceToggle(service.keyId)}
										disabled={disabled || isInactive}
									/>
								}
								label={
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
										<Typography
											variant="body2"
											sx={{
												textDecoration: isInactive ? 'line-through' : 'none',
												color: isInactive ? 'text.secondary' : 'text.primary',
											}}
										>
											{getServiceDescription(service)}
										</Typography>
										{isInactive && (
											<Typography variant="caption" color="error">
												Servicio inactivo
											</Typography>
										)}
									</Box>
								}
							/>
						);
					})}
				</FormGroup>
			)}
		</Box>
	);
};
